import {School, SchoolClass, Subscription} from "models"
import {useCallback, useEffect, useMemo} from "react"
import Devise from "@kaspernj/api-maker/build/devise"
import ModelEvents from "@kaspernj/api-maker/build/model-events"
import moment from "shared/moment"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useShape from "set-state-compare/build/use-shape"

const loadExpiredProTrial = async (s) => {
  if (!shouldLoadAnythingProData(s)) {
    s.set({expiredProTrial: null})

    return
  }

  const thirtyDaysAgo = moment().subtract(30, "days").toDate()
  const subscription = await Subscription
    .ransack({
      plan_identifier_eq: "pro_trial",
      contact_id_eq: s.m.currentUser.contactId(),
      without_active_period: true,
      last_period_ends_at_gteq: thirtyDaysAgo
    })
    .select({Subscription: ["createdAt"]})
    .first()

  s.set({expiredProTrial: subscription})
}

const loadSchoolsWithPro = async (s) => {
  if (!shouldLoadAnythingProData(s)) {
    s.set({schoolsWithPro: null})

    return
  }

  const schoolsWithPro = await School
    .ransack({
      contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolsWithPro})
}

const loadSchoolClassesWithPro = async (s) => {
  if (!shouldLoadAnythingProData(s)) {
    s.set({schoolClassesWithPro: null})

    return
  }

  const schoolClassesWithPro = await SchoolClass
    .ransack({
      contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolClassesWithPro})
}

const loadSchoolsThroughClassWithSchoolPro = async (s) => {
  if (!shouldLoadAnythingProData(s)) {
    s.set({schoolsThroughClassWithSchoolPro: null})

    return
  }

  const schoolsThroughClassWithSchoolPro = await School
    .ransack({
      school_classes_contact_children_relationships_child_id_eq: s.m.currentUser.contactId(),
      subscriptions_plan_identifier_eq: "pro",
      with_active_subscription: true
    })
    .toArray()

  s.set({schoolsThroughClassWithSchoolPro})
}

const cloneSelect = (select) => {
  if (select?.clone) {
    return select.clone()
  }

  return JSON.parse(JSON.stringify(select))
}

const hasAlignedSignedInUser = (currentUser) => {
  const deviseCurrentUser = Devise.currentUser()

  return Boolean(
    currentUser?.id?.() &&
    deviseCurrentUser?.id?.() &&
    Devise.isUserSignedIn() &&
    currentUser.id() == deviseCurrentUser.id()
  )
}

const shouldLoadAnythingProData = (s) => hasAlignedSignedInUser(s.m.currentUser)

const loadUserProSubscriptions = async (s) => {
  if (!shouldLoadAnythingProData(s)) {
    s.set({hasProTrial: false, trialSubscription: null, userProSubscriptions: null})

    return
  }

  const userProSubscriptionsQuery = await Subscription
    .ransack({
      plan_identifier_eq_any: ["pro", "pro_plus", "pro_trial"],
      contact_id_eq: s.m.currentUser.contactId(),
      with_active_period: true
    })

  if (s.props?.userSubscriptions?.select) {
    userProSubscriptionsQuery.select(cloneSelect(s.props.userSubscriptions.select))
  } else {
    userProSubscriptionsQuery.select({Subscription: ["id"]})
  }

  const preloads = new Set(["plan"])
  const extraPreloads = s.props?.userSubscriptions?.preload

  if (extraPreloads) {
    if (Array.isArray(extraPreloads)) {
      extraPreloads.forEach((preload) => preloads.add(preload))
    } else {
      preloads.add(extraPreloads)
    }
  }

  userProSubscriptionsQuery.preload(Array.from(preloads))

  const userProSubscriptions = await userProSubscriptionsQuery.toArray()
  const trialSubscription = userProSubscriptions.find((subscription) => subscription.plan()?.identifier() == "pro_trial")

  s.set({
    hasProTrial: Boolean(trialSubscription),
    trialSubscription,
    userProSubscriptions
  })
}

const useAnythingPro = (props) => {
  const s = useShape(props)
  const currentUserFromContext = useCurrentUser()
  const currentUser = props?.currentUser || currentUserFromContext

  s.meta.currentUser = currentUser
  s.useStates({
    anythingPro: null,
    expiredProTrial: null,
    hasProTrial: false,
    schoolClassesWithPro: null,
    schoolsWithPro: null,
    schoolsThroughClassWithSchoolPro: null,
    trialSubscription: null,
    userProSubscriptions: null
  })

  if (
    (
      s.s.schoolsWithPro?.length > 0 ||
      s.s.schoolClassesWithPro?.length > 0 ||
      s.s.userProSubscriptions?.length > 0 ||
      s.s.schoolsThroughClassWithSchoolPro?.length > 0
    ) &&
    s.s.anythingPro !== true
  ) {
    s.set({anythingPro: true})
  } else if (
    s.s.schoolsWithPro?.length === 0 &&
    s.s.schoolClassesWithPro?.length === 0 &&
    s.s.userProSubscriptions?.length === 0 &&
    s.s.schoolsThroughClassWithSchoolPro?.length === 0 &&
    s.s.anythingPro !== false
  ) {
    s.set({anythingPro: false})
  }

  useMemo(() => {
    if (hasAlignedSignedInUser(currentUser)) {
      loadExpiredProTrial(s)
      loadSchoolsThroughClassWithSchoolPro(s)
      loadSchoolClassesWithPro(s)
      loadSchoolsWithPro(s)
      loadUserProSubscriptions(s)
    } else {
      s.set({
        expiredProTrial: null,
        hasProTrial: false,
        schoolClassesWithPro: null,
        schoolsThroughClassWithSchoolPro: null,
        schoolsWithPro: null,
        trialSubscription: null,
        userProSubscriptions: null
      })
    }
  }, [currentUser?.id()])

  const onStartProUserTrial = useCallback(() => {
    loadUserProSubscriptions(s)
  }, [])

  const onStartProUser = useCallback(() => {
    loadUserProSubscriptions(s)
  }, [])

  useEffect(() => {
    if (hasAlignedSignedInUser(currentUser)) {
      const webSocketSubscription = ModelEvents.connect(currentUser, "start_pro_user_trial", onStartProUserTrial)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [currentUser?.id()])

  useEffect(() => {
    if (hasAlignedSignedInUser(currentUser)) {
      const webSocketSubscription = ModelEvents.connect(currentUser, "start_pro_user", onStartProUser)

      return () => {
        webSocketSubscription.unsubscribe()
      }
    }
  }, [currentUser?.id()])

  const computedAnythingPro = Boolean(
    s.s.anythingPro ||
    s.s.hasProTrial ||
    s.s.userProSubscriptions?.length > 0 ||
    s.s.schoolsWithPro?.length > 0 ||
    s.s.schoolClassesWithPro?.length > 0 ||
    s.s.schoolsThroughClassWithSchoolPro?.length > 0
  )

  useEffect(() => {
    if (computedAnythingPro) {
      // eslint-disable-next-line no-underscore-dangle
      globalThis.__anythingPro = true
    } else if (
      // eslint-disable-next-line no-underscore-dangle
      globalThis.__anythingPro == null
    ) {
      // eslint-disable-next-line no-underscore-dangle
      globalThis.__anythingPro = false
    }
  }, [computedAnythingPro])

  return s.state
}

export default useAnythingPro
